"use client";

import Image from "next/image";
import { useState } from "react";
import { Grid3X3, Maximize, PaintBucket } from "lucide-react";
import { ROUTES } from "@/lib/routes";

const beigeVariantImages = [
  "/variants-section/cd3ee7f7ec9d217b571ff88edaeef308c7b77ffc.png",
  "/variants-section/522dceb7639ee556302d6085094e41d630f1727c.png",
  "/variants-section/4d073657f71c205aacd793ee05b617b44c3e5156.png",
  "/variants-section/e0a813d855ed9b8305950298acf0d776f44a991a.png",
] as const;

const denimVariantImages = [
  "/variants-section/63271ee120f1f22c673dd6ab2d63c44d86ab89dd.png",
  "/variants-section/103653153b83fcdcee5b9c375f9ccf95a83b14af.png",
  "/variants-section/da6941bdda0b258b5cdef39a0bf10dd2280e2d13.png",
  "/variants-section/e916fc83f594c2c1696a80d0e53e7e6c47d7b211.png",
] as const;

const navyVariantImages = [
  "/variants-section/afad600d1d48430d0c7a983ab0db675905471348.png",
  "/variants-section/4af7bc303f58e045733f834e37f8d755bdbec456.png",
  "/variants-section/f1fb588ffb85d3606913a6d956f907f715087123.png",
] as const;

const variantImages = [
  ...beigeVariantImages,
  ...denimVariantImages,
  ...navyVariantImages,
] as const;

type VariantDimension = "color" | "size" | "shape";

const filterPills = [
  { id: "color", label: "Color", icon: PaintBucket },
  { id: "size", label: "Size", icon: Maximize },
  { id: "shape", label: "Shape", icon: Grid3X3 },
] as const;

const swatches = [
  { color: "#9A7E68", background: "rgba(154, 126, 104, 0.32)", active: true },
  { color: "#14E8FF", background: "rgba(20, 232, 255, 0.32)", active: false },
  { color: "#788A94", background: "rgba(120, 138, 148, 0.32)", active: true },
  { color: "#1A2730", background: "rgba(26, 39, 48, 0.32)", active: true },
  { color: "#FF6614", background: "rgba(255, 102, 20, 0.32)", active: false },
] as const;

const sizeOptions = ["Small", "Medium", "Large"] as const;
const shapeOptions = ["Round", "Square", "Tall"] as const;

function VariantImageTile({ src, index, featured = false }: { src: string; index: number; featured?: boolean }) {
  return (
    <div
      data-testid="product-variant-image"
      className={`relative shrink-0 overflow-hidden bg-[#F7F8FB] shadow-[0_10px_28px_rgba(27,28,48,0.08)] ${
        featured ? "h-[220px] w-[220px] rounded-[20px] sm:h-[300px] sm:w-[300px]" : "aspect-square rounded-3xl"
      }`}
    >
      <Image
        src={src}
        alt={`Generated product variant example ${index + 1}`}
        fill
        sizes={featured ? "(max-width: 640px) 220px, 300px" : "(max-width: 768px) 45vw, 229px"}
        className="object-cover"
      />
    </div>
  );
}

function FilterPill({
  label,
  icon: Icon,
  active,
  onClick,
}: (typeof filterPills)[number] & { active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex h-9 items-center justify-center gap-2 rounded-full border px-4 text-sm font-semibold leading-[18px] ${
        active
          ? "border-[#1464FF] bg-[rgba(20,100,255,0.04)] text-[#1464FF]"
          : "border-[#E0EAF4] bg-white text-[#4D4D5C]"
      } transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1464FF]`}
    >
      <Icon aria-hidden="true" className="h-5 w-5" strokeWidth={1.8} />
      {label}
    </button>
  );
}

function ColorSwatches() {
  return (
    <div className="flex flex-wrap items-center gap-3" aria-label="Example product colours">
      {swatches.map((swatch) => (
        <span
          key={swatch.color}
          data-testid="variant-color-swatch"
          className="h-9 w-9 rounded-full"
          style={{
            background: swatch.background,
            border: swatch.active ? `4px solid ${swatch.color}` : "0 solid transparent",
          }}
        />
      ))}
    </div>
  );
}

function OptionChips({ options, label }: { options: readonly string[]; label: string }) {
  return (
    <div className="flex flex-wrap items-center gap-3" aria-label={label}>
      {options.map((option) => (
        <span
          key={option}
          className="inline-flex h-9 items-center justify-center rounded-full border border-[#E0EAF4] bg-white px-4 text-sm font-semibold leading-[18px] text-[#4D4D5C]"
        >
          {option}
        </span>
      ))}
    </div>
  );
}

function VariantControls({ selectedDimension }: { selectedDimension: VariantDimension }) {
  if (selectedDimension === "color") {
    return <ColorSwatches />;
  }

  if (selectedDimension === "size") {
    return <OptionChips label="Example product sizes" options={sizeOptions} />;
  }

  return <OptionChips label="Example product shapes" options={shapeOptions} />;
}

function TryYourselfCallout() {
  return (
    <div className="hidden md:absolute md:left-[306px] md:top-[220px] md:flex md:items-start md:gap-2">
      <svg
        aria-hidden="true"
        className="mt-2 h-[32px] w-[93px] overflow-visible text-[#1B1C30]"
        viewBox="0 0 93 32"
        fill="none"
      >
        <path
          d="M90 2C75.5 18.5 53.5 22.5 30 11.5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M32.5 5.5L18.5 8.5L28.5 18.5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="whitespace-nowrap text-sm font-semibold leading-[18px] text-[#1B1C30]">Try Yourself</span>
    </div>
  );
}

export default function ProductVariantsSection() {
  const [selectedDimension, setSelectedDimension] = useState<VariantDimension>("color");

  return (
    <section className="relative isolate flex w-full flex-col items-center gap-12 overflow-hidden bg-white px-5 py-16 sm:px-8 md:gap-[72px] md:py-20 lg:px-[150px]">
      <div className="flex w-full max-w-[1141px] flex-col items-center gap-8 md:flex-row md:items-start md:gap-14">
        <VariantImageTile src={variantImages[0]} index={0} featured />

        <div className="relative flex min-w-0 flex-1 flex-col items-center gap-6 text-center md:items-start md:text-left">
          <div className="flex w-full flex-col gap-4">
            <h2 className="text-[32px] font-bold leading-[42px] text-[#1B1C30] sm:text-[40px] sm:leading-[52px]">
              Design Product Variants in Every Colour, Size, and Style
            </h2>
            <p className="max-w-[785px] text-base leading-[21px] text-[#4D4D5C]">
              Easily create multiple versions of your product with different colors, sizes, and styles. Our AI helps
              you generate consistent, high-quality visuals tailored to every variation, so you can showcase your full
              catalog without extra design effort.
            </p>
          </div>

          <div className="flex flex-col items-center gap-4 md:items-start">
            <div className="flex flex-wrap items-center justify-center gap-3 md:justify-start">
              {filterPills.map((pill) => (
                <FilterPill
                  key={pill.id}
                  {...pill}
                  active={selectedDimension === pill.id}
                  onClick={() => setSelectedDimension(pill.id)}
                />
              ))}
            </div>
            <VariantControls selectedDimension={selectedDimension} />
          </div>

          <TryYourselfCallout />
        </div>
      </div>

      <div className="pointer-events-none absolute left-0 right-0 top-[52%] z-10 hidden h-[242px] bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,#FFFFFF_100%)] md:block" />

      <div className="relative z-20 grid w-full max-w-[1141px] grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-4 lg:gap-[32px_75px]">
        {variantImages.slice(0, 11).map((src, index) => (
          <VariantImageTile key={`${src}-${index}`} src={src} index={index} />
        ))}

        <a
          href={ROUTES.homePricing}
          className="flex aspect-square items-center justify-center rounded-3xl border-2 border-dashed border-[#1464FF] bg-[rgba(20,100,255,0.16)] px-4 text-center text-base font-semibold leading-[21px] text-[#1464FF] transition-colors hover:bg-[rgba(20,100,255,0.22)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1464FF]"
        >
          Get Started Now
        </a>
      </div>
    </section>
  );
}
